import React, { useEffect, useState } from 'react'
import { Button, ListGroup, Modal, Tab, Tabs } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toastService } from '../../services/toast.service'
import { workflowService } from '../../services/workflow.service'
import { Workflow, WorkflowAction, WorkflowActionType, WorkflowTrigger, WorkflowTriggerType } from '../../types'
import { parseBackendErrors } from '../../utils/errorHandler'
import { InputSwitch } from '../common/InputSwitch'
import { InputText } from '../common/InputText'
import { ActionEditDialog } from './ActionEditDialog'
import { TriggerEditDialog } from './TriggerEditDialog'
import './WorkflowEditDialog.scss'

interface WorkflowEditDialogProps {
  workflow?: Workflow | null
  isCreating?: boolean
  onSuccess: (workflow: Workflow) => void
  onError: (error: any) => void
  onClose: () => void
}

interface WorkflowFormData {
  name: string
  order: number
  enabled: boolean
  triggers: WorkflowTrigger[]
  actions: WorkflowAction[]
}

export const WorkflowEditDialog: React.FC<WorkflowEditDialogProps> = ({ workflow, isCreating = false, onSuccess, onError, onClose }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState<string>('triggers')
  const [showTriggerDialog, setShowTriggerDialog] = useState(false)
  const [showActionDialog, setShowActionDialog] = useState(false)
  const [editingTrigger, setEditingTrigger] = useState<WorkflowTrigger | null>(null)
  const [editingAction, setEditingAction] = useState<WorkflowAction | null>(null)

  const [formData, setFormData] = useState<WorkflowFormData>({
    name: '',
    order: 0,
    enabled: true,
    triggers: [],
    actions: [],
  })

  const isEdit = !isCreating && !!workflow

  useEffect(() => {
    loadData()

    if (workflow) {
      setFormData({
        name: workflow.name || '',
        order: workflow.order || 0,
        enabled: workflow.enabled ?? true,
        triggers: workflow.triggers || [],
        actions: workflow.actions || [],
      })
    } else {
      setFormData({
        name: '',
        order: 0,
        enabled: true,
        triggers: [],
        actions: [],
      })
    }
    setErrors({})
  }, [workflow, isCreating])

  const loadData = async () => {
    try {
    } catch (error) {}
  }

  const handleInputChange = (field: keyof WorkflowFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSave = async () => {
    setLoading(true)
    setErrors({})

    try {
      // Clean triggers - remove filters and ensure proper structure
      const cleanedTriggers = formData.triggers.map((trigger) => {
        const cleaned: any = { ...trigger }
        delete cleaned.filters
        // List of filter fields that should be kept even if null or empty array (needed for deletion)
        const filterFields = [
          'filter_custom_field_query',
          'filter_has_correspondent',
          'filter_has_document_type',
          'filter_has_storage_path',
          'filter_has_tags',
          'filter_has_all_tags',
          'filter_has_not_tags',
          'filter_has_not_correspondents',
          'filter_has_not_document_types',
          'filter_has_not_storage_paths',
          'filter_workflow_status',
          'filter_has_not_workflow_statuses',
        ]
        // Remove empty arrays and null values, but keep filter fields if they're null or empty array (needed for deletion)
        Object.keys(cleaned).forEach((key) => {
          if (Array.isArray(cleaned[key]) && cleaned[key].length === 0 && !filterFields.includes(key)) {
            delete cleaned[key]
          } else if ((cleaned[key] === null || cleaned[key] === undefined) && !filterFields.includes(key)) {
            delete cleaned[key]
          }
        })
        // Explicitly set filter fields to null or empty array if they exist and are null/empty
        filterFields.forEach((field) => {
          if (cleaned[field] === null || cleaned[field] === undefined) {
            // For array fields, set to empty array; for single value fields, set to null
            if (field.includes('tags') || field.includes('not_')) {
              cleaned[field] = []
            } else {
              cleaned[field] = null
            }
          } else if (Array.isArray(cleaned[field]) && cleaned[field].length === 0) {
            // Keep empty arrays for filter fields
            cleaned[field] = []
          }
        })
        return cleaned
      })

      // Clean actions - set email and webhook to null if action type doesn't match;
      // for Assignment, explicitly send assign_workflow_status (null when cleared) so backend updates it
      const cleanedActions = formData.actions.map((action) => {
        const cleaned: any = { ...action }
        if (cleaned.type !== WorkflowActionType.Webhook) {
          cleaned.webhook = null
        }
        if (cleaned.type !== WorkflowActionType.Email) {
          cleaned.email = null
        }
        if (cleaned.type === WorkflowActionType.Assignment) {
          cleaned.assign_workflow_status = cleaned.assign_workflow_status ?? null
        }
        return cleaned
      })

      const data = {
        name: formData.name,
        order: formData.order,
        enabled: formData.enabled,
        triggers: cleanedTriggers,
        actions: cleanedActions,
      }

      let result: Workflow
      if (isEdit && workflow && workflow.id) {
        result = await workflowService.updateWorkflow(workflow.id, data)
        toastService.showSuccess(t('successfully_updated', { name: result.name }))
      } else {
        result = await workflowService.createWorkflow(data)
        toastService.showSuccess(t('successfully_created', { name: result.name }))
      }

      onSuccess(result)
    } catch (error: any) {
      const { fieldErrors, errorResponse } = parseBackendErrors(error, t, t('error_saving_workflow'))

      setErrors(fieldErrors)
      toastService.showError(t('error_saving_workflow'), errorResponse)

      if (!error.response?.data) {
        onError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  const getTitle = () => {
    return isEdit ? t('edit_workflow') : t('create_workflow')
  }

  const handleAddTrigger = () => {
    setEditingTrigger(null)
    setShowTriggerDialog(true)
  }

  const handleEditTrigger = (trigger: WorkflowTrigger) => {
    setEditingTrigger(trigger)
    setShowTriggerDialog(true)
  }

  const handleDeleteTrigger = (index: number) => {
    const newTriggers = formData.triggers.filter((_, i) => i !== index)
    setFormData((prev) => ({ ...prev, triggers: newTriggers }))
  }

  const handleTriggerSuccess = (trigger: WorkflowTrigger) => {
    if (editingTrigger) {
      const index = formData.triggers.findIndex((t) => t === editingTrigger)
      const newTriggers = [...formData.triggers]
      newTriggers[index] = trigger
      setFormData((prev) => ({ ...prev, triggers: newTriggers }))
    } else {
      setFormData((prev) => ({ ...prev, triggers: [...prev.triggers, trigger] }))
    }
    setShowTriggerDialog(false)
  }

  const handleAddAction = () => {
    setEditingAction(null)
    setShowActionDialog(true)
  }

  const handleEditAction = (action: WorkflowAction) => {
    setEditingAction(action)
    setShowActionDialog(true)
  }

  const handleDeleteAction = (index: number) => {
    const newActions = formData.actions.filter((_, i) => i !== index)
    setFormData((prev) => ({ ...prev, actions: newActions }))
  }

  const handleActionSuccess = (action: WorkflowAction) => {
    if (editingAction) {
      const index = formData.actions.findIndex((a) => a === editingAction)
      const newActions = [...formData.actions]
      newActions[index] = action
      setFormData((prev) => ({ ...prev, actions: newActions }))
    } else {
      setFormData((prev) => ({ ...prev, actions: [...prev.actions, action] }))
    }
    setShowActionDialog(false)
  }

  const getTriggerDisplayName = (trigger: WorkflowTrigger) => {
    switch (trigger.type) {
      case WorkflowTriggerType.Consumption:
        return t('consumption')
      case WorkflowTriggerType.DocumentAdded:
        return t('document_added')
      case WorkflowTriggerType.DocumentUpdated:
        return t('document_updated')
      case WorkflowTriggerType.Scheduled:
        return t('scheduled')
      case WorkflowTriggerType.DocumentWorkflowStatusChanged:
        return t('trigger_type_document_workflow_status_changed')
      default:
        return String(trigger.type)
    }
  }

  const getActionDisplayName = (action: WorkflowAction) => {
    switch (action.type) {
      case WorkflowActionType.Assignment:
        return t('assignment')
      case WorkflowActionType.Removal:
        return t('removal')
      case WorkflowActionType.Email:
        return t('email')
      case WorkflowActionType.Webhook:
        return t('webhook')
      case WorkflowActionType.AttributeAndActionPermissions:
        return t('workflow_action_attribute_action_permissions')
      default:
        return String(action.type)
    }
  }

  return (
    <Modal show={true} onHide={onClose} size="xl" backdrop="static" className="workflow-edit-dialog">
      <Modal.Header closeButton>
        <Modal.Title>
          {getTitle()}
          {workflow?.id && <span className="badge bg-primary ms-2">ID: {workflow.id}</span>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row">
          <div className="col-md-6">
            <InputText
              title={t('name')}
              value={formData.name}
              onChange={(value) => handleInputChange('name', value)}
              error={errors.name}
              autocomplete="off"
            />
          </div>

          <div className="col-md-3">
            <InputText
              title={t('sort_order')}
              type="number"
              value={formData.order}
              onChange={(value) => handleInputChange('order', parseInt(value) || 0)}
              error={errors.order}
            />
          </div>

          <div className="col-md-3">
            <InputSwitch title={t('enabled')} value={formData.enabled} onChange={(value) => handleInputChange('enabled', value)} />
          </div>
        </div>

        <Tabs activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)} className="workflow-tabs">
          <Tab
            eventKey="triggers"
            title={
              <span>
                <i className="bi bi-lightning me-2"></i>
                {t('triggers')}
                {formData.triggers.length > 0 && <span className="badge bg-primary ms-2">{formData.triggers.length}</span>}
              </span>
            }
          >
            <div className="workflow-tab-content">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-1">{t('run_workflow_on')}</h6>
                  <p className="text-muted mb-0 small">{t('triggers_info')}</p>
                </div>
                <Button variant="primary" size="sm" onClick={handleAddTrigger}>
                  <i className="bi bi-plus-circle me-1"></i>
                  {t('add_trigger')}
                </Button>
              </div>

              {formData.triggers.length > 0 && (
                <ListGroup>
                  {formData.triggers.map((trigger, index) => (
                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center flex-grow-1">
                        <span className="badge bg-primary me-3">{index + 1}</span>
                        <div className="flex-grow-1">
                          <Button
                            variant="link"
                            className="p-0 text-primary fw-bold text-decoration-none text-start"
                            onClick={() => handleEditTrigger(trigger)}
                          >
                            {getTriggerDisplayName(trigger)}
                          </Button>
                        </div>
                      </div>
                      <div className="btn-group">
                        <Button variant="outline-secondary" size="sm" onClick={() => handleEditTrigger(trigger)} title={t('edit')}>
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteTrigger(index)} title={t('delete')}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

              {formData.triggers.length === 0 && (
                <div className="text-muted text-center py-5">
                  <i className="bi bi-lightning-charge display-4 d-block mb-2 opacity-50"></i>
                  <p className="mb-0">{t('no_triggers_defined')}</p>
                  <Button variant="outline-primary" size="sm" className="mt-3" onClick={handleAddTrigger}>
                    <i className="bi bi-plus-circle me-1"></i>
                    {t('add_trigger')}
                  </Button>
                </div>
              )}
            </div>
          </Tab>

          <Tab
            eventKey="actions"
            title={
              <span>
                <i className="bi bi-gear me-2"></i>
                {t('actions')}
                {formData.actions.length > 0 && <span className="badge bg-primary ms-2">{formData.actions.length}</span>}
              </span>
            }
          >
            <div className="workflow-tab-content">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-1">{t('apply_actions')}</h6>
                  <p className="text-muted mb-0 small">{t('actions_info')}</p>
                </div>
                <Button variant="primary" size="sm" onClick={handleAddAction}>
                  <i className="bi bi-plus-circle me-1"></i>
                  {t('add_action')}
                </Button>
              </div>

              {formData.actions.length > 0 && (
                <ListGroup>
                  {formData.actions.map((action, index) => (
                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center flex-grow-1">
                        <span className="badge bg-primary me-3">{index + 1}</span>
                        <div className="flex-grow-1">
                          <Button
                            variant="link"
                            className="p-0 text-primary fw-bold text-decoration-none text-start"
                            onClick={() => handleEditAction(action)}
                          >
                            {getActionDisplayName(action)}
                          </Button>
                        </div>
                      </div>
                      <div className="btn-group">
                        <Button variant="outline-secondary" size="sm" onClick={() => handleEditAction(action)} title={t('edit')}>
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteAction(index)} title={t('delete')}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

              {formData.actions.length === 0 && (
                <div className="text-muted text-center py-5">
                  <i className="bi bi-gear display-4 d-block mb-2 opacity-50"></i>
                  <p className="mb-0">{t('no_actions_defined')}</p>
                  <Button variant="outline-primary" size="sm" className="mt-3" onClick={handleAddAction}>
                    <i className="bi bi-plus-circle me-1"></i>
                    {t('add_action')}
                  </Button>
                </div>
              )}
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <div className="w-100">
          {errors._general && (
            <div className="alert alert-danger mb-2" role="alert">
              <strong>{t('error')}:</strong> {errors._general}
            </div>
          )}
          {Object.keys(errors).filter((key) => key.startsWith('triggers') && key !== '_general').length > 0 && (
            <div className="alert alert-danger mb-2" role="alert">
              {Object.keys(errors)
                .filter((key) => key.startsWith('triggers') && key !== '_general')
                .map((key) => (
                  <div key={key} className="mb-1">
                    {errors[key]}
                  </div>
                ))}
            </div>
          )}
          {Object.keys(errors).filter((key) => key.startsWith('actions') && key !== '_general').length > 0 && (
            <div className="alert alert-danger mb-2" role="alert">
              {Object.keys(errors)
                .filter((key) => key.startsWith('actions') && key !== '_general')
                .map((key) => (
                  <div key={key} className="mb-1">
                    {errors[key]}
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onClose} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
            {t('save')}
          </Button>
        </div>
      </Modal.Footer>

      {showTriggerDialog && (
        <TriggerEditDialog trigger={editingTrigger} onSuccess={handleTriggerSuccess} onError={() => {}} onClose={() => setShowTriggerDialog(false)} />
      )}

      {showActionDialog && (
        <ActionEditDialog action={editingAction} onSuccess={handleActionSuccess} onError={() => {}} onClose={() => setShowActionDialog(false)} />
      )}
    </Modal>
  )
}
